import React from "react";
import { Icon, mergeStyles } from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/merge-styles";
import { getIconColors } from "../TestResultNode";
import { IRcaMtaStsValidationStatus } from "../../models/IRcaMtaStsValidationStatus";
import { DeferredTooltipHost } from "../DeferredTooltipHost";

/**
 * @param status The status to map into the icon for the sections.
 * @returns The icon to apply.
 */
const getIconForSectionStatus = (status: IRcaMtaStsValidationStatus) => {
  const mapping = {
    [IRcaMtaStsValidationStatus.ConfigurationNotPresent]: "ValidationWarningIcon",
    [IRcaMtaStsValidationStatus.InvalidConfiguation]: "StatusErrorFull",
    [IRcaMtaStsValidationStatus.Success]: "CompletedSolid",
  };

  return mapping[status];
};

/**
 * @param status The status to map into the icon.
 * @returns The icon to apply.
 */
const getIconForStatus = (status: IRcaMtaStsValidationStatus) => {
  const mapping = {
    [IRcaMtaStsValidationStatus.ConfigurationNotPresent]: "ValidationWarningIcon",
    [IRcaMtaStsValidationStatus.InvalidConfiguation]: "BoxMultiplySolid",
    [IRcaMtaStsValidationStatus.Success]: "CheckboxCompositeReversed",
  };

  return mapping[status];
};

/**
 * @param props The component properties
 * @returns The markup
 */
export const MtaStsValidationResultIcon: React.FC<{
  /**
   * MTA-STS status
   */
  mtaStsStatus: IRcaMtaStsValidationStatus;

  /**
   * MTA-STS details to show to tooltip.
   */
  mtaStsDetails: string;
}> = ({ mtaStsStatus, mtaStsDetails }) => {
  const iconColors = getIconColors();
  const classNames = mergeStyleSets({
    iconHeader: {
      padding: 0,
      fontSize: 16,
      height: 16,
    },
    iconSuccess: {
      color: iconColors.Success,
    },
    iconError: {
      color: iconColors.Error,
    },
    iconWarning: {
      color: iconColors.SuccessWithWarningsMid,
    },
  });
  const icon = getIconForStatus(mtaStsStatus);

  const style =
    mtaStsStatus === IRcaMtaStsValidationStatus.ConfigurationNotPresent
      ? classNames.iconWarning
      : mtaStsStatus === IRcaMtaStsValidationStatus.InvalidConfiguation
      ? classNames.iconError
      : classNames.iconSuccess;

  return (
    <DeferredTooltipHost content={mtaStsDetails} aria-hidden={true} styles={{ root: classNames.iconHeader }}>
      <Icon iconName={icon} styles={{ root: mergeStyles(style, classNames.iconHeader) }} />
    </DeferredTooltipHost>
  );
};

/**
 * @param props The component properties
 * @returns The markup
 */
export const MtaStsValidationResultSectionIcon: React.FC<{
  /**
   * MTA-STS status
   */
  mtaStsStatus: IRcaMtaStsValidationStatus;
}> = ({ mtaStsStatus }) => {
  const iconColors = getIconColors();
  const classNames = mergeStyleSets({
    iconHeader: {
      padding: 0,
      fontSize: 16,
      height: 16,
    },
    iconSuccess: {
      color: iconColors.Success,
    },
    iconError: {
      color: iconColors.Error,
    },
    iconWarning: {
      color: iconColors.SuccessWithWarningsMid,
    },
  });
  const icon = getIconForSectionStatus(mtaStsStatus);

  const style =
    mtaStsStatus === IRcaMtaStsValidationStatus.ConfigurationNotPresent
      ? classNames.iconWarning
      : mtaStsStatus === IRcaMtaStsValidationStatus.InvalidConfiguation
      ? classNames.iconError
      : classNames.iconSuccess;

  return <Icon iconName={icon} styles={{ root: mergeStyles(style, classNames.iconHeader) }} />;
};
